/**
 * Редактор легенды карты в админке `/mapsadmin` (Требования 6.1, 6.2, 6.3, 6.4, 6.7).
 *
 * Для выбранной карты администратор управляет элементами легенды: добавляет,
 * редактирует и удаляет их (Требование 6.1). У каждого элемента — иконка
 * (загружаемое изображение через `POST /api/mapsadmin/upload/image`), название и
 * краткое описание (Требования 6.2, 6.7), а также развёрнутое rich-text описание,
 * редактируемое встроенным редактором Quill, подключаемым по CDN (Требование
 * 6.3). Вставка изображений в развёрнутое описание загружает файл на сервер и
 * сохраняет в контенте ссылку на него (Требование 6.4). Перед сохранением
 * rich-text проходит санитизацию ({@link sanitizeRichHtml}), в БД хранится
 * безопасный HTML.
 *
 * Архитектура «тонкий DOM, чистое ядро» (как в {@link MapsAdminPage}): вся логика
 * преобразования значений формы в полезную нагрузку API, разбора ответов сервера
 * и вычисления порядка сортировки вынесена в чистые, покрытые юнит-тестами
 * функции ({@link buildLegendItemPayload}, {@link legendItemToFormValues},
 * {@link parseLegendList}, {@link parseUploadResponse}, {@link nextSortOrder}).
 * DOM-слой лишь читает значения полей, вызывает эти функции и отображает
 * результат. Работа с Quill изолирована в {@link createQuillRichTextEditor}, а
 * при отсутствии CDN редактор вырождается в textarea-заглушку
 * ({@link createFallbackRichTextEditor}) — юнит-тесты не требуют загрузки CDN.
 *
 * Требования: 6.1, 6.2, 6.3, 6.4, 6.7
 */

import type { LegendItem } from '@langton/shared';
import { sanitizeRichHtml } from './legend-panel.js';

// ---------------------------------------------------------------------------
// Чистое ядро (без DOM/Quill) — покрывается юнит-тестами
// ---------------------------------------------------------------------------

/**
 * Сырые значения формы элемента легенды. Все поля — строки, приведённые
 * DOM-слоем; `iconUrl` и `richDescription` заполняются загрузкой/редактором.
 */
export interface LegendFormValues {
  /** Название элемента легенды. */
  name: string;
  /** Краткое описание. */
  shortDescription: string;
  /** Ссылка на загруженную иконку (URL с сервера) либо пустая строка. */
  iconUrl: string;
  /** Развёрнутое rich-text описание в виде HTML (из редактора) либо пустая строка. */
  richDescription: string;
}

/**
 * Результат {@link buildLegendItemPayload}: полезная нагрузка для создания или
 * обновления элемента легенды (без `id`/`mapId`) либо перечень ошибок.
 */
export interface BuildLegendPayloadResult {
  /** Готовая полезная нагрузка либо `null`, если есть ошибки. */
  payload: Omit<LegendItem, 'id' | 'mapId'> | null;
  /** Сообщения об ошибках валидации (пусто при успехе). */
  errors: string[];
}

/** Нормализовать необязательную строку: `trim`, пустую строку → `undefined`. */
function optionalTrimmed(value: string | undefined | null): string | undefined {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? undefined : trimmed;
}

/**
 * Построить полезную нагрузку элемента легенды из значений формы (чистая функция).
 *
 * Порядок:
 * 1. Проверить обязательное название (Требование 6.2).
 * 2. Санитизировать развёрнутое описание ({@link sanitizeRichHtml}) — в БД
 *    попадает только безопасный HTML (Требования 6.3, 6.4).
 * 3. Необязательные поля (иконка, краткое и развёрнутое описание) с пустыми
 *    значениями опускаются (`undefined`).
 *
 * @param values значения формы
 * @param sortOrder желаемый порядок сортировки (по умолчанию 0)
 * @returns полезная нагрузка при успехе либо перечень ошибок
 */
export function buildLegendItemPayload(
  values: LegendFormValues,
  sortOrder = 0,
): BuildLegendPayloadResult {
  const errors: string[] = [];

  const name = (values.name ?? '').trim();
  if (name === '') {
    errors.push('Укажите название элемента легенды.');
  }

  const iconUrl = optionalTrimmed(values.iconUrl);
  const shortDescription = optionalTrimmed(values.shortDescription);

  // Санитизация rich-text перед сохранением (Требования 6.3, 6.4).
  const sanitized = sanitizeRichHtml(values.richDescription ?? '').trim();
  const richDescription = sanitized === '' ? undefined : sanitized;

  const safeSort = Number.isFinite(sortOrder) ? sortOrder : 0;

  if (errors.length > 0) {
    return { payload: null, errors };
  }

  return {
    payload: {
      iconUrl,
      name,
      shortDescription,
      richDescription,
      sortOrder: safeSort,
    },
    errors: [],
  };
}

/**
 * Обратный маппинг: значения формы из существующего элемента легенды для
 * заполнения полей при редактировании.
 *
 * @param item элемент легенды
 * @returns значения для полей формы
 */
export function legendItemToFormValues(item: LegendItem): LegendFormValues {
  return {
    name: item.name ?? '',
    shortDescription: item.shortDescription ?? '',
    iconUrl: item.iconUrl ?? '',
    richDescription: item.richDescription ?? '',
  };
}

/**
 * Разобрать ответ `GET /api/mapsadmin/maps/:id/legend` в массив элементов
 * легенды, нормализуя поля и отбрасывая некорректные записи. Результат
 * сортируется по `sortOrder`, затем по названию (стабильный порядок списка,
 * Требование 6.1).
 *
 * @param json разобранный JSON ответа
 * @returns нормализованный отсортированный список элементов легенды
 */
export function parseLegendList(json: unknown): LegendItem[] {
  if (!Array.isArray(json)) {
    return [];
  }
  const items: LegendItem[] = [];
  for (const entry of json) {
    if (!entry || typeof entry !== 'object') {
      continue;
    }
    const e = entry as Record<string, unknown>;
    if (typeof e.id !== 'string' || e.id === '') {
      continue;
    }
    items.push({
      id: e.id,
      mapId: typeof e.mapId === 'string' ? e.mapId : '',
      iconUrl: typeof e.iconUrl === 'string' && e.iconUrl !== '' ? e.iconUrl : undefined,
      name: typeof e.name === 'string' ? e.name : 'Без названия',
      shortDescription:
        typeof e.shortDescription === 'string' && e.shortDescription !== ''
          ? e.shortDescription
          : undefined,
      richDescription:
        typeof e.richDescription === 'string' && e.richDescription !== ''
          ? e.richDescription
          : undefined,
      sortOrder:
        typeof e.sortOrder === 'number' && Number.isFinite(e.sortOrder)
          ? e.sortOrder
          : 0,
    });
  }
  items.sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) {
      return a.sortOrder - b.sortOrder;
    }
    return a.name.localeCompare(b.name);
  });
  return items;
}

/**
 * Извлечь URL загруженного файла из ответа `POST /api/mapsadmin/upload/image`
 * (`{ url: string }`, Требования 6.4, 6.7).
 *
 * @param json разобранный JSON ответа
 * @returns URL строкой либо `null`, если формат некорректен
 */
export function parseUploadResponse(json: unknown): string | null {
  if (!json || typeof json !== 'object') {
    return null;
  }
  const url = (json as Record<string, unknown>).url;
  return typeof url === 'string' && url !== '' ? url : null;
}

/**
 * Вычислить `sortOrder` для нового элемента легенды: на единицу больше
 * максимального среди существующих (добавление в конец списка).
 *
 * @param items текущие элементы легенды
 * @returns следующий порядковый номер
 */
export function nextSortOrder(items: readonly LegendItem[]): number {
  let max = -1;
  for (const item of items) {
    if (Number.isFinite(item.sortOrder) && item.sortOrder > max) {
      max = item.sortOrder;
    }
  }
  return max + 1;
}

// ---------------------------------------------------------------------------
// Изоляция rich-text редактора (Quill по CDN с textarea-заглушкой)
// ---------------------------------------------------------------------------

/**
 * Минимальный интерфейс rich-text редактора, используемый DOM-слоем. Скрывает
 * различия между Quill (CDN) и запасной textarea, чтобы остальной код не зависел
 * от конкретной реализации и мог тестироваться без CDN.
 */
export interface RichTextEditor {
  /** Текущий HTML содержимого редактора. */
  getHtml(): string;
  /** Установить HTML содержимого редактора. */
  setHtml(html: string): void;
  /** Снять обработчики и очистить DOM редактора. */
  destroy(): void;
}

/** Загрузчик изображений: принимает файл, возвращает URL на сервере. */
export type ImageUploader = (file: File) => Promise<string>;

/** Минимальный интерфейс конструктора Quill, доступного глобально через CDN. */
interface QuillLike {
  root: HTMLElement;
  getModule(name: string): { addHandler(name: string, handler: () => void): void };
  getSelection(focus?: boolean): { index: number } | null;
  insertEmbed(index: number, type: string, value: string): void;
  clipboard: { dangerouslyPasteHTML(html: string): void };
}

interface QuillConstructor {
  new (container: HTMLElement, options: unknown): QuillLike;
}

/**
 * Создать rich-text редактор на базе Quill, если он загружен по CDN
 * (Требование 6.3). Регистрирует обработчик кнопки вставки изображения, который
 * загружает файл на сервер и вставляет в контент ссылку на него (Требование 6.4).
 *
 * Возвращает `null`, если Quill недоступен (тогда DOM-слой использует заглушку).
 *
 * @param container контейнер для монтирования редактора
 * @param uploadImage загрузчик изображений (файл → URL)
 * @returns адаптер {@link RichTextEditor} либо `null`
 */
export function createQuillRichTextEditor(
  container: HTMLElement,
  uploadImage: ImageUploader,
): RichTextEditor | null {
  const Quill = (globalThis as { Quill?: QuillConstructor }).Quill;
  if (typeof Quill !== 'function') {
    return null;
  }

  const editorEl = document.createElement('div');
  container.appendChild(editorEl);

  const quill = new Quill(editorEl, {
    theme: 'snow',
    modules: {
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        ['clean'],
      ],
    },
  });

  // Обработчик вставки изображения: загрузить на сервер, вставить ссылку
  // в контент (Требование 6.4).
  const imageHandler = (): void => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (): void => {
      const file = input.files?.[0];
      if (!file) {
        return;
      }
      void uploadImage(file)
        .then((url) => {
          const range = quill.getSelection(true);
          const index = range ? range.index : 0;
          quill.insertEmbed(index, 'image', url);
        })
        .catch(() => {
          // Ошибка загрузки не должна ломать редактор; сообщение покажет DOM-слой.
        });
    };
    input.click();
  };
  quill.getModule('toolbar').addHandler('image', imageHandler);

  return {
    getHtml: () => quill.root.innerHTML,
    setHtml: (html: string) => {
      quill.clipboard.dangerouslyPasteHTML(html ?? '');
    },
    destroy: () => {
      editorEl.remove();
    },
  };
}

/**
 * Запасной rich-text редактор на основе `<textarea>` (HTML вводится напрямую),
 * с кнопкой вставки изображения, загружающей файл на сервер. Используется, когда
 * Quill недоступен (например, CDN не загрузился), чтобы редактирование
 * развёрнутого описания оставалось возможным.
 *
 * @param container контейнер для монтирования
 * @param uploadImage загрузчик изображений (файл → URL)
 * @returns адаптер {@link RichTextEditor}
 */
export function createFallbackRichTextEditor(
  container: HTMLElement,
  uploadImage: ImageUploader,
): RichTextEditor {
  const wrapper = document.createElement('div');
  wrapper.className = 'legend-rich-fallback';

  const textarea = document.createElement('textarea');
  textarea.rows = 8;
  textarea.placeholder = 'HTML развёрнутого описания…';

  const imgButton = document.createElement('button');
  imgButton.type = 'button';
  imgButton.className = 'btn btn-sm';
  imgButton.textContent = 'Вставить изображение';
  imgButton.addEventListener('click', () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (): void => {
      const file = input.files?.[0];
      if (!file) {
        return;
      }
      void uploadImage(file)
        .then((url) => {
          // Вставить <img> в позицию курсора (Требование 6.4).
          const start = textarea.selectionStart ?? textarea.value.length;
          const end = textarea.selectionEnd ?? textarea.value.length;
          const tag = `<img src="${url}" alt="">`;
          textarea.value =
            textarea.value.slice(0, start) + tag + textarea.value.slice(end);
        })
        .catch(() => {
          // Ошибку загрузки покажет DOM-слой.
        });
    };
    input.click();
  });

  wrapper.appendChild(imgButton);
  wrapper.appendChild(textarea);
  container.appendChild(wrapper);

  return {
    getHtml: () => textarea.value,
    setHtml: (html: string) => {
      textarea.value = html ?? '';
    },
    destroy: () => {
      wrapper.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// Тонкий DOM-слой: редактор легенды
// ---------------------------------------------------------------------------

/** Опции конфигурации {@link LegendEditor}. */
export interface LegendEditorOptions {
  /** Базовый URL API (без завершающего слэша). По умолчанию пустая строка. */
  apiBase?: string;
  /** Функция загрузки (внедрение для тестов). По умолчанию глобальный `fetch`. */
  fetchFn?: typeof fetch;
  /** Провайдер заголовков авторизации (из общей админ-сессии). */
  authHeaders?: () => Record<string, string>;
  /** Колбэк для показа сообщений пользователю (успех/ошибка). */
  onMessage?: (text: string, type: 'success' | 'error') => void;
  /** Колбэк при ответе 401/403 (сессия истекла) — показать модалку входа. */
  onUnauthorized?: () => void;
  /**
   * Фабрика rich-text редактора (внедрение для тестов). По умолчанию Quill по
   * CDN с откатом на textarea-заглушку.
   */
  richTextFactory?: (container: HTMLElement, uploadImage: ImageUploader) => RichTextEditor;
}

/** Небольшой помощник создания DOM-элемента с атрибутами и потомками. */
function createEl<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  children: Array<Node | string> = [],
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (key === 'class') {
      el.className = value;
    } else {
      el.setAttribute(key, value);
    }
  }
  for (const child of children) {
    el.append(child);
  }
  return el;
}

/**
 * Редактор легенды карты: модалка со списком элементов и модалка формы элемента
 * (иконка, название, краткое описание, rich-text развёрнутое описание). Строит
 * собственный DOM в переданном контейнере и обслуживает CRUD через
 * `/api/mapsadmin/maps/:id/legend[/:itemId]` и загрузку изображений через
 * `/api/mapsadmin/upload/image`.
 *
 * Жизненный цикл:
 * 1. `new LegendEditor(container, options)` — строит модалки (если доступен DOM).
 * 2. `open(mapId, mapName)` — загрузка элементов и показ списка (Требование 6.1).
 * 3. добавление/редактирование/удаление — через форму и соответствующие
 *    эндпоинты; иконка и изображения описания загружаются на сервер.
 */
export class LegendEditor {
  private readonly container: HTMLElement;
  private readonly apiBase: string;
  private readonly fetchFn: typeof fetch;
  private readonly getAuthHeaders: () => Record<string, string>;
  private readonly onMessage: (text: string, type: 'success' | 'error') => void;
  private readonly onUnauthorized: () => void;
  private readonly richTextFactory: (
    container: HTMLElement,
    uploadImage: ImageUploader,
  ) => RichTextEditor;

  private mapId: string | null = null;
  private mapName = '';
  private items: LegendItem[] = [];
  private editingItemId: string | null = null;
  private richEditor: RichTextEditor | null = null;

  // DOM-ссылки.
  private listModal: HTMLElement | null = null;
  private listTitle: HTMLElement | null = null;
  private listBody: HTMLElement | null = null;
  private itemModal: HTMLElement | null = null;
  private itemTitle: HTMLElement | null = null;
  private itemError: HTMLElement | null = null;
  private richContainer: HTMLElement | null = null;
  private fields: {
    name: HTMLInputElement;
    shortDescription: HTMLTextAreaElement;
    iconUrl: HTMLInputElement;
    iconPreview: HTMLImageElement;
    iconFile: HTMLInputElement;
  } | null = null;

  constructor(container: HTMLElement, options: LegendEditorOptions = {}) {
    this.container = container;
    this.apiBase = options.apiBase ?? '';
    this.fetchFn =
      options.fetchFn ??
      (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : (undefined as never));
    this.getAuthHeaders = options.authHeaders ?? (() => ({}));
    this.onMessage = options.onMessage ?? (() => undefined);
    this.onUnauthorized = options.onUnauthorized ?? (() => undefined);
    this.richTextFactory =
      options.richTextFactory ??
      ((c, upload) =>
        createQuillRichTextEditor(c, upload) ?? createFallbackRichTextEditor(c, upload));

    if (typeof document !== 'undefined') {
      this.buildDom();
    }
  }

  /**
   * Открыть редактор легенды для карты: загрузить элементы и показать список
   * (Требование 6.1).
   *
   * @param mapId идентификатор карты
   * @param mapName название карты (для заголовка)
   */
  async open(mapId: string, mapName: string): Promise<void> {
    this.mapId = mapId;
    this.mapName = mapName;
    if (this.listTitle) {
      this.listTitle.textContent = `Легенда: ${mapName}`;
    }
    this.listModal?.classList.add('active');
    await this.loadItems();
  }

  /** Закрыть модалку списка легенды. */
  private closeList(): void {
    this.listModal?.classList.remove('active');
  }

  /** Загрузить элементы легенды карты и отрисовать список. */
  async loadItems(): Promise<void> {
    if (!this.mapId) {
      return;
    }
    try {
      const response = await this.fetchFn(
        `${this.apiBase}/api/mapsadmin/maps/${encodeURIComponent(this.mapId)}/legend`,
        { headers: this.getAuthHeaders() },
      );
      if (response.status === 401 || response.status === 403) {
        this.onUnauthorized();
        return;
      }
      if (!response.ok) {
        throw new Error(`код ${response.status}`);
      }
      this.items = parseLegendList(await response.json());
      this.renderList();
    } catch (error) {
      this.onMessage(
        'Ошибка загрузки легенды: ' +
          (error instanceof Error ? error.message : String(error)),
        'error',
      );
    }
  }

  /** Отрисовать список элементов легенды. */
  private renderList(): void {
    const body = this.listBody;
    if (!body) {
      return;
    }
    body.textContent = '';
    if (this.items.length === 0) {
      body.append(createEl('p', { class: 'legend-empty' }, ['Элементов легенды пока нет.']));
    }
    for (const item of this.items) {
      const icon = createEl('img', {
        class: 'legend-item-icon',
        alt: '',
      }) as HTMLImageElement;
      if (item.iconUrl) {
        icon.src = item.iconUrl;
      } else {
        icon.style.display = 'none';
      }

      const editBtn = createEl('button', { class: 'btn btn-sm', type: 'button' }, ['✏️']);
      editBtn.addEventListener('click', () => this.openItemModal(item.id));
      const deleteBtn = createEl(
        'button',
        { class: 'btn btn-sm btn-danger', type: 'button' },
        ['🗑️'],
      );
      deleteBtn.addEventListener('click', () => void this.deleteItem(item.id, item.name));

      const row = createEl('div', { class: 'legend-item-row' }, [
        icon,
        createEl('div', { class: 'legend-item-meta' }, [
          createEl('div', { class: 'legend-item-name' }, [item.name]),
          createEl('div', { class: 'legend-item-short' }, [item.shortDescription ?? '']),
        ]),
        createEl('div', { class: 'legend-item-actions' }, [editBtn, deleteBtn]),
      ]);
      body.append(row);
    }
  }

  // -------------------------------------------------------------------------
  // Форма элемента легенды (создание/редактирование)
  // -------------------------------------------------------------------------

  /**
   * Открыть форму элемента легенды. При `itemId === null` — создание нового,
   * иначе — редактирование существующего (Требование 6.1).
   */
  openItemModal(itemId: string | null = null): void {
    this.editingItemId = itemId;
    const existing = itemId ? this.items.find((i) => i.id === itemId) ?? null : null;
    const values: LegendFormValues = existing
      ? legendItemToFormValues(existing)
      : { name: '', shortDescription: '', iconUrl: '', richDescription: '' };

    if (this.itemTitle) {
      this.itemTitle.textContent = existing
        ? 'Редактировать элемент легенды'
        : 'Новый элемент легенды';
    }
    this.applyItemValues(values);
    this.mountRichEditor(values.richDescription);
    this.itemModal?.classList.add('active');
  }

  /** Заполнить поля формы значениями. */
  private applyItemValues(values: LegendFormValues): void {
    const f = this.fields;
    if (!f) {
      return;
    }
    f.name.value = values.name;
    f.shortDescription.value = values.shortDescription;
    f.iconUrl.value = values.iconUrl;
    if (values.iconUrl) {
      f.iconPreview.src = values.iconUrl;
      f.iconPreview.style.display = '';
    } else {
      f.iconPreview.removeAttribute('src');
      f.iconPreview.style.display = 'none';
    }
    f.iconFile.value = '';
    if (this.itemError) {
      this.itemError.textContent = '';
    }
  }

  /** Смонтировать rich-text редактор с начальным содержимым. */
  private mountRichEditor(initialHtml: string): void {
    if (!this.richContainer) {
      return;
    }
    this.richEditor?.destroy();
    this.richContainer.textContent = '';
    this.richEditor = this.richTextFactory(this.richContainer, (file) =>
      this.uploadImage(file),
    );
    this.richEditor.setHtml(initialHtml);
  }

  /** Закрыть форму элемента и уничтожить редактор. */
  private closeItemModal(): void {
    this.itemModal?.classList.remove('active');
    this.richEditor?.destroy();
    this.richEditor = null;
    this.editingItemId = null;
  }

  /** Считать значения формы из полей DOM. */
  private readItemValues(): LegendFormValues {
    const f = this.fields;
    if (!f) {
      throw new Error('Форма не инициализирована');
    }
    return {
      name: f.name.value,
      shortDescription: f.shortDescription.value,
      iconUrl: f.iconUrl.value,
      richDescription: this.richEditor?.getHtml() ?? '',
    };
  }

  /**
   * Сохранить элемент легенды: собрать полезную нагрузку
   * ({@link buildLegendItemPayload}), при ошибках показать их; иначе — `POST`
   * (создание) или `PUT` (обновление). Развёрнутое описание санитизируется
   * перед отправкой (Требования 6.3, 6.4).
   */
  private async saveItem(): Promise<void> {
    if (!this.mapId) {
      return;
    }
    let values: LegendFormValues;
    try {
      values = this.readItemValues();
    } catch {
      return;
    }

    const isEdit = this.editingItemId !== null;
    const existing = isEdit
      ? this.items.find((i) => i.id === this.editingItemId) ?? null
      : null;
    const sortOrder = existing ? existing.sortOrder : nextSortOrder(this.items);

    const { payload, errors } = buildLegendItemPayload(values, sortOrder);
    if (!payload) {
      if (this.itemError) {
        this.itemError.textContent = errors.join(' ');
      }
      return;
    }

    const base = `${this.apiBase}/api/mapsadmin/maps/${encodeURIComponent(this.mapId)}/legend`;
    const url = isEdit
      ? `${base}/${encodeURIComponent(this.editingItemId as string)}`
      : base;
    try {
      const response = await this.fetchFn(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (response.status === 401 || response.status === 403) {
        this.onUnauthorized();
        return;
      }
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as
          | { error?: string }
          | null;
        const detail = body?.error || `код ${response.status}`;
        if (this.itemError) {
          this.itemError.textContent = detail;
        }
        return;
      }
      this.closeItemModal();
      this.onMessage(isEdit ? 'Элемент легенды обновлён' : 'Элемент легенды создан', 'success');
      await this.loadItems();
    } catch (error) {
      if (this.itemError) {
        this.itemError.textContent =
          error instanceof Error ? error.message : String(error);
      }
    }
  }

  /** Удалить элемент легенды после подтверждения (Требование 6.1). */
  async deleteItem(itemId: string, name: string): Promise<void> {
    if (!this.mapId) {
      return;
    }
    const confirmFn = typeof window !== 'undefined' ? window.confirm : () => true;
    if (!confirmFn(`Удалить элемент легенды «${name}» и связанные с ним метки?`)) {
      return;
    }
    try {
      const response = await this.fetchFn(
        `${this.apiBase}/api/mapsadmin/maps/${encodeURIComponent(this.mapId)}/legend/${encodeURIComponent(itemId)}`,
        { method: 'DELETE', headers: this.getAuthHeaders() },
      );
      if (response.status === 401 || response.status === 403) {
        this.onUnauthorized();
        return;
      }
      if (!response.ok && response.status !== 204) {
        throw new Error(`код ${response.status}`);
      }
      this.onMessage('Элемент легенды удалён', 'success');
      await this.loadItems();
    } catch (error) {
      this.onMessage(
        'Ошибка: ' + (error instanceof Error ? error.message : String(error)),
        'error',
      );
    }
  }

  // -------------------------------------------------------------------------
  // Загрузка изображений (иконка + изображения описания)
  // -------------------------------------------------------------------------

  /**
   * Загрузить изображение на сервер (`POST /api/mapsadmin/upload/image`) и вернуть
   * URL сохранённого файла (Требования 6.4, 6.7). Тип и размер валидируются на
   * сервере; при ошибке пробрасывается исключение.
   *
   * @param file файл изображения
   * @returns URL загруженного изображения
   */
  async uploadImage(file: File): Promise<string> {
    const form = new FormData();
    form.append('image', file);
    const response = await this.fetchFn(`${this.apiBase}/api/mapsadmin/upload/image`, {
      method: 'POST',
      // Content-Type для multipart/form-data проставляет браузер (с boundary).
      headers: { ...this.getAuthHeaders() },
      body: form,
    });
    if (response.status === 401 || response.status === 403) {
      this.onUnauthorized();
      throw new Error('Сессия истекла');
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { error?: string } | null;
      throw new Error(body?.error || `Ошибка загрузки (код ${response.status})`);
    }
    const url = parseUploadResponse(await response.json());
    if (!url) {
      throw new Error('Сервер не вернул ссылку на изображение');
    }
    return url;
  }

  /** Обработать выбор файла иконки: загрузить и показать превью (Требование 6.2). */
  private async handleIconFile(): Promise<void> {
    const f = this.fields;
    if (!f || !f.iconFile.files || f.iconFile.files.length === 0) {
      return;
    }
    const file = f.iconFile.files[0];
    try {
      const url = await this.uploadImage(file);
      f.iconUrl.value = url;
      f.iconPreview.src = url;
      f.iconPreview.style.display = '';
    } catch (error) {
      if (this.itemError) {
        this.itemError.textContent =
          'Не удалось загрузить иконку: ' +
          (error instanceof Error ? error.message : String(error));
      }
    }
  }

  // -------------------------------------------------------------------------
  // Построение DOM
  // -------------------------------------------------------------------------

  /** Построить модалки списка легенды и формы элемента. */
  private buildDom(): void {
    this.container.append(this.buildListModal());
    this.container.append(this.buildItemModal());
  }

  private buildListModal(): HTMLElement {
    this.listTitle = createEl('h2', {}, ['Легенда']);
    this.listBody = createEl('div', { class: 'legend-list' });

    const addBtn = createEl('button', { class: 'btn btn-primary', type: 'button' }, [
      '+ Добавить элемент',
    ]);
    addBtn.addEventListener('click', () => this.openItemModal(null));

    const closeBtn = createEl('button', { class: 'close-btn', type: 'button' }, ['×']);
    closeBtn.addEventListener('click', () => this.closeList());

    const content = createEl('div', { class: 'modal-content' }, [
      createEl('div', { class: 'modal-header' }, [this.listTitle, closeBtn]),
      addBtn,
      this.listBody,
    ]);
    const modal = createEl('div', { class: 'modal', id: 'legendModal' }, [content]);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.closeList();
      }
    });
    this.listModal = modal;
    return modal;
  }

  private buildItemModal(): HTMLElement {
    const name = createEl('input', { type: 'text', required: 'true' }) as HTMLInputElement;
    const shortDescription = createEl('textarea', {
      rows: '2',
    }) as HTMLTextAreaElement;

    const iconUrl = createEl('input', { type: 'hidden' }) as HTMLInputElement;
    const iconPreview = createEl('img', {
      class: 'legend-icon-preview',
      alt: '',
    }) as HTMLImageElement;
    iconPreview.style.display = 'none';
    const iconFile = createEl('input', {
      type: 'file',
      accept: 'image/*',
    }) as HTMLInputElement;
    iconFile.addEventListener('change', () => void this.handleIconFile());

    this.richContainer = createEl('div', { class: 'legend-rich-container' });

    this.fields = { name, shortDescription, iconUrl, iconPreview, iconFile };
    this.itemError = createEl('div', { class: 'modal-error' });
    this.itemTitle = createEl('h2', {}, ['Новый элемент легенды']);

    const closeBtn = createEl('button', { class: 'close-btn', type: 'button' }, ['×']);
    closeBtn.addEventListener('click', () => this.closeItemModal());

    const form = createEl('form', {}, [
      this.buildField('Название', name),
      this.buildField('Краткое описание', shortDescription),
      this.buildField('Иконка', createEl('div', {}, [iconPreview, iconFile, iconUrl])),
      this.buildField('Развёрнутое описание', this.richContainer),
      this.itemError,
      createEl('button', { class: 'btn btn-primary', type: 'submit' }, ['Сохранить']),
    ]) as HTMLFormElement;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      void this.saveItem();
    });

    const content = createEl('div', { class: 'modal-content' }, [
      createEl('div', { class: 'modal-header' }, [this.itemTitle, closeBtn]),
      form,
    ]);
    const modal = createEl('div', { class: 'modal', id: 'legendItemModal' }, [content]);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.closeItemModal();
      }
    });
    this.itemModal = modal;
    return modal;
  }

  private buildField(labelText: string, control: HTMLElement): HTMLElement {
    return createEl('div', { class: 'form-group' }, [
      createEl('label', {}, [labelText]),
      control,
    ]);
  }
}
